import React, { SyntheticEvent, ReactNode } from 'react';
import { Button, ButtonProps } from '@ui-library/button';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMessage } from '@hooks/useMessage';
import { Copy } from '@ui-library/icons';

/** Accessible name for the icon-only button, which has no visible label of its own. */
const DEFAULT_COPY_LABEL = 'Copy to clipboard';

/** Shown when the browser refuses the write, for example outside a secure context. */
const COPY_FAILED_MESSAGE = "Couldn't copy. Select the text and copy it.";

/** Fields both variants accept. The `type` key alone discriminates them. */
type CopyToClipboardBaseProps = {
  content: string;
  message?: string;
  className?: string;
  onCopy?: () => void;
  /** Automation hook — callers pass a unique id since this button repeats across pages. */
  testId?: string;
  /** Overrides the accessible name — worth setting when the page has several copy buttons. */
  ariaLabel?: string;
};

type CopyToClipboardProps =
  | (CopyToClipboardBaseProps & {
      id?: string;
      children?: ReactNode;
      showIcon?: boolean;
      size?: ButtonProps['size'];
      /**
       * `next` draws it as the redesigned secondary button with the library's copy glyph, and
       * waits for the browser: if the browser refuses the write it says so and skips onCopy.
       */
      variant?: 'next';
    })
  | (CopyToClipboardBaseProps & { type: 'icon' });

export const CopyToClipboard = (props: CopyToClipboardProps) => {
  const { message, context } = useMessage();

  const isNext = !('type' in props) && props.variant === 'next';

  const onClick = async (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isNext) {
      try {
        await navigator.clipboard.writeText(props.content);
      } catch {
        message('error', COPY_FAILED_MESSAGE, 3);
        return;
      }
    } else {
      navigator.clipboard.writeText(props.content);
    }
    message('info', props.message ?? 'Copied', 1);
    props.onCopy?.();
  };

  if ('type' in props) {
    return (
      <>
        {context}
        <Button
          shape="circle"
          type="text"
          icon={<FontAwesomeIcon icon={regular('copy')} />}
          onClick={onClick}
          className={props.className}
          data-testid={props.testId}
          aria-label={props.ariaLabel ?? DEFAULT_COPY_LABEL}
        />
      </>
    );
  }

  const { id, className, children, showIcon = true, size, testId, ariaLabel, variant } = props;

  return (
    <>
      {context}
      <Button
        type="default"
        variant={variant}
        level="secondary"
        id={id}
        icon={showIcon && (isNext ? <Copy /> : <FontAwesomeIcon icon={regular('copy')} />)}
        onClick={onClick}
        className={className}
        size={size}
        data-testid={testId}
        // Only label it when there is no visible text — an aria-label would otherwise
        // override the button's own children for screen readers.
        aria-label={ariaLabel ?? (children ? undefined : DEFAULT_COPY_LABEL)}
      >
        {children}
      </Button>
    </>
  );
};
